import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Share2, Copy, Check } from "lucide-react";
import { toast } from "sonner";

// Simple, colour-accurate WhatsApp + Telegram brand marks.
const WA = (props) => (
  <svg viewBox="0 0 32 32" fill="currentColor" {...props}>
    <path d="M16.001 3.2c-7.07 0-12.8 5.73-12.8 12.8 0 2.26.6 4.47 1.72 6.42L3.2 28.8l6.55-1.68a12.75 12.75 0 006.25 1.62h.001c7.07 0 12.799-5.729 12.799-12.8 0-3.42-1.33-6.63-3.75-9.05a12.71 12.71 0 00-9.05-3.75zm0 23.36h-.001a10.55 10.55 0 01-5.38-1.47l-.386-.23-3.887 1L7.4 22.1l-.25-.4a10.56 10.56 0 011.62-13.19 10.55 10.55 0 0114.94 0 10.5 10.5 0 013.09 7.48c0 5.87-4.77 10.65-10.799 10.65zm5.79-7.97c-.318-.16-1.877-.926-2.17-1.03-.29-.106-.5-.16-.71.16-.212.32-.816 1.03-1 1.24-.185.212-.37.24-.688.08-.317-.16-1.34-.494-2.55-1.573-.943-.84-1.58-1.879-1.765-2.197-.185-.317-.02-.489.14-.649.144-.144.317-.376.476-.564.16-.187.212-.32.318-.531.106-.212.053-.398-.027-.558-.08-.16-.71-1.71-.975-2.34-.256-.616-.517-.532-.71-.542l-.606-.011c-.212 0-.556.08-.847.398-.29.318-1.113 1.088-1.113 2.651 0 1.564 1.139 3.075 1.298 3.287.16.212 2.246 3.43 5.44 4.807.76.328 1.352.523 1.815.67.762.243 1.455.209 2.003.127.611-.091 1.877-.767 2.142-1.508.265-.74.265-1.373.185-1.508-.08-.135-.291-.212-.61-.372z"/>
  </svg>
);
const TG = (props) => (
  <svg viewBox="0 0 32 32" fill="currentColor" {...props}>
    <path d="M16 3.2C8.933 3.2 3.2 8.933 3.2 16S8.933 28.8 16 28.8 28.8 23.067 28.8 16 23.067 3.2 16 3.2zm5.94 8.83l-1.998 9.42c-.15.665-.55.828-1.113.516l-3.077-2.27-1.484 1.428c-.164.164-.302.302-.618.302l.22-3.127 5.7-5.15c.247-.219-.054-.34-.383-.121l-7.043 4.432-3.03-.947c-.66-.207-.673-.66.138-.977l11.83-4.562c.548-.196 1.032.132.858 1.056z"/>
  </svg>
);

export default function ShareMenu({ url, title, body, testid = "share-menu" }) {
  const [copied, setCopied] = useState(false);
  const shareText = title ? `${title}\n\n${body || ""}\n${url}` : `${body || ""}\n${url}`;

  const openWhatsapp = () => {
    const u = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
    window.open(u, "_blank", "noopener,noreferrer");
  };
  const openTelegram = () => {
    const u = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title || body || "")}`;
    window.open(u, "_blank", "noopener,noreferrer");
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Could not copy — long-press to select");
    }
  };
  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text: body, url });
      } catch { /* user cancelled */ }
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          data-testid={testid}
          variant="ghost"
          size="sm"
          className="text-[color:var(--ayur-ink-2)] hover:text-[color:var(--ayur-emerald)] gap-1"
          title="Share"
          onClick={(e) => { if (navigator.share && e.detail === 2) nativeShare(); }}
        >
          <Share2 className="w-4 h-4" /> Share
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48 bg-white border-[color:var(--ayur-border)]">
        <DropdownMenuItem data-testid={`${testid}-whatsapp`} onSelect={openWhatsapp} className="cursor-pointer gap-2">
          <WA className="w-4 h-4 text-[#25D366]" /> WhatsApp
        </DropdownMenuItem>
        <DropdownMenuItem data-testid={`${testid}-telegram`} onSelect={openTelegram} className="cursor-pointer gap-2">
          <TG className="w-4 h-4 text-[#229ED9]" /> Telegram
        </DropdownMenuItem>
        <DropdownMenuItem data-testid={`${testid}-copy`} onSelect={copy} className="cursor-pointer gap-2">
          {copied ? <Check className="w-4 h-4 text-[color:var(--ayur-emerald)]" /> : <Copy className="w-4 h-4" />}
          {copied ? "Copied" : "Copy link"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
